// src/components/instructor/ops/TeamMemberOpsControls.js
//
// Pass 57B (items 3/11/20) — compact per-member ops controls for the Team directory: category
// (School/Collective), tri-state teaching capabilities (Ski/Snowboard/Guiding — UNKNOWN never silently
// NO), and calendar visibility (via ScheduleVisibilityControl). All writes are authorised (owner/admin)
// and record the actor; they modify the ORG/season relationship (the member doc), not global identity.
// Visibility restrictions apply only to School; Collective is self-managed; Unclassified must be
// categorised before an org calendar policy applies.

import React, { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../firebase';
import { useAuth } from '../../../AuthContext';
import { authorizeOrgWrite, assertOrgWriteAuthorized } from '../../../utils/operationalWriteGate';
import { memberDocId } from '../../../utils/booking';
import { setSeasonCategoryPatch, resolveInstructorCategory, CATEGORY, CURRENT_SEASON_ID } from '../../../utils/instructorCategory';
import { capabilityState, setCapabilityPatch, CAPABILITY_KEYS } from '../../../utils/instructorCapabilities';
import ScheduleVisibilityControl from './ScheduleVisibilityControl';
import { teamAccessUiState, TEAM_UI_STATE_LABEL } from '../../../utils/teamAccess';

const STATE_CYCLE = { unknown: 'yes', yes: 'no', no: 'unknown' };
const STATE_GLYPH = { yes: '✓', no: '✕', unknown: '—' };
const CAP_LABEL = { ski: 'Ski', snowboard: 'Snowboard', guiding: 'Guiding' };

export default function TeamMemberOpsControls({ member, organizationId, seasonId = CURRENT_SEASON_ID }) {
  const { currentUser, userData } = useAuth() || {};
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const canManage = authorizeOrgWrite({ userData, organizationId, action: 'team_access' }).ok;
  if (!member) return null;

  const category = (() => {
    const s = resolveInstructorCategory(member, { season: seasonId });
    return s !== CATEGORY.UNCLASSIFIED ? s : resolveInstructorCategory(member, {});
  })();
  const docId = member.id || memberDocId(member.userId, organizationId);

  const write = async (patch, okMsg) => {
    setMsg('');
    try { assertOrgWriteAuthorized({ userData, organizationId, action: 'team_access' }); }
    catch { setMsg('Not authorised'); return; }
    setBusy(true);
    try {
      await updateDoc(doc(db, 'organizationMembers', docId), {
        ...patch, updatedBy: currentUser?.uid || '', updatedByName: userData?.name || '', updatedAt: serverTimestamp(),
      });
      setMsg(okMsg);
    } catch (e) { setMsg(e?.message || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'center', fontSize: 12 }} data-testid="team-member-ops">
      {/* Category (season relationship) */}
      <span>
        Category:{' '}
        <select value={category} disabled={!canManage || busy} data-testid="category-select"
          onChange={(e) => write(setSeasonCategoryPatch(member, e.target.value, seasonId), 'Category saved')}>
          <option value="school">School</option>
          <option value="collective">Collective</option>
          {category === 'unclassified' && <option value="unclassified" disabled>Unclassified — set one</option>}
        </select>
      </span>

      {/* Tri-state capabilities */}
      <span data-testid="capabilities">
        Capabilities:{' '}
        {CAPABILITY_KEYS.map((k) => {
          const st = capabilityState(member, k);
          return (
            <button key={k} type="button" disabled={!canManage || busy} title={`${CAP_LABEL[k]}: ${st}`}
              onClick={() => write(setCapabilityPatch(member, k, STATE_CYCLE[st]), 'Capability saved')}
              style={{ marginRight: 4, padding: '2px 6px', borderRadius: 4, cursor: 'pointer',
                border: '1px solid #ccc', background: st === 'yes' ? '#DCFCE7' : st === 'no' ? '#FEE2E2' : '#F3F4F6' }}>
              {CAP_LABEL[k]} {STATE_GLYPH[st]}
            </button>
          );
        })}
      </span>

      {/* Calendar visibility — school only; collective self-managed; unclassified needs category first */}
      {category === 'collective' ? (
        <span style={{ color: '#6B7280' }}>Calendar: Self-managed (Collective)</span>
      ) : category === 'unclassified' ? (
        <span style={{ color: '#B45309' }}>Set category before calendar policy applies</span>
      ) : (
        <ScheduleVisibilityControl mode="member" organizationId={organizationId} member={member} seasonId={seasonId} />
      )}

      {msg && <span style={{ color: '#6B7280' }}>{msg}</span>}

      {/* Pass 58C — Team shows only the ACCESS STATUS; the invite/role/revoke mutation surface now lives
          in ONE canonical place: Organisation → Settings → Access & Invitations (no duplicate here). */}
      <span data-testid="team-access-status" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#6B7280' }}>
        Access: <strong style={{ color: '#57534E' }}>{TEAM_UI_STATE_LABEL[teamAccessUiState(member)]}</strong>
        <span style={{ fontSize: 11, color: '#9E9B95' }}>· Manage in Settings → Access &amp; Invitations</span>
      </span>
    </div>
  );
}
